import { mapFromInternalAuditScope } from 'mappings/internalAuditScopeMappings';
import { mapToInternalAuditTemplate } from 'mappings/internalAuditTemplateMappings';
import InternalAuditMonitoringUpgradePreviewDTO, {
  InternalAuditMonitoringSourceDTO,
  InternalAuditMonitoringUpgradePreviewRequestDTO,
  InternalAuditMonitoringUpgradeProcedureDTO,
  InternalAuditMonitoringUpgradeProcedureScopeItemDTO,
  InternalAuditMonitoringUpgradeProcedureSourceDTO,
  InternalAuditMonitoringUpgradeRequestDTO,
  InternalAuditMonitoringUpgradeResultDTO,
  InternalAuditMonitoringUpgradeScopeSourceDTO,
  InternalAuditMonitoringUpgradeSourceDTO,
  InternalAuditMonitoringUpgradeSourceVersionDTO,
  InternalAuditMonitoringUpgradeTeamCandidateDTO,
} from 'models/dto/internalAuditMonitoringUpgradeDTO';
import { InternalAuditScopeIdsDTO } from 'models/dto/internalAuditScopeDTO';
import Entity, { EntityTypes } from 'models/entity';
import InternalAuditMonitoringUpgradePreview, {
  InternalAuditMonitoringSource,
  InternalAuditMonitoringUpgradePreviewRequest,
  InternalAuditMonitoringUpgradeProcedure,
  InternalAuditMonitoringUpgradeProcedureScopeItem,
  InternalAuditMonitoringUpgradeProcedureSource,
  InternalAuditMonitoringUpgradeRequest,
  InternalAuditMonitoringUpgradeResult,
  InternalAuditMonitoringUpgradeRolePermission,
  InternalAuditMonitoringUpgradeScopeSource,
  InternalAuditMonitoringUpgradeSource,
  InternalAuditMonitoringUpgradeTeamCandidate,
} from 'models/internalAuditMonitoringUpgrade';
import RecurringPattern from 'models/recurringPattern';
import { fromApiDateTimeOptional } from 'utils/datetime';

function mapScopeItemsToIds(scopeItems: Entity[]): InternalAuditScopeIdsDTO {
  const dto = new InternalAuditScopeIdsDTO();
  dto.controlIds = scopeItems
    .filter((scopeItem) => [EntityTypes.Requirement, EntityTypes.Control].includes(scopeItem.typeOfEntity))
    .map((scopeItem) => scopeItem.entityId);
  dto.processIds = scopeItems
    .filter((scopeItem) => scopeItem.typeOfEntity === EntityTypes.Process)
    .map((scopeItem) => scopeItem.entityId);
  dto.riskIds = scopeItems
    .filter((scopeItem) => scopeItem.typeOfEntity === EntityTypes.Risk)
    .map((scopeItem) => scopeItem.entityId);
  dto.assetIds = scopeItems
    .filter((scopeItem) => scopeItem.typeOfEntity === EntityTypes.Asset)
    .map((scopeItem) => scopeItem.entityId);
  dto.processingActivityIds = scopeItems
    .filter((scopeItem) => scopeItem.typeOfEntity === EntityTypes.ProcessingActivity)
    .map((scopeItem) => scopeItem.entityId);
  dto.objectiveIds = scopeItems
    .filter((scopeItem) => scopeItem.typeOfEntity === EntityTypes.Objective)
    .map((scopeItem) => scopeItem.entityId);
  dto.linkIds = scopeItems
    .filter((scopeItem) => scopeItem.typeOfEntity === EntityTypes.Link)
    .map((scopeItem) => scopeItem.entityId);

  return dto;
}

export function mapToInternalAuditMonitoringUpgradePreviewRequest(
  item: InternalAuditMonitoringUpgradePreviewRequest,
): InternalAuditMonitoringUpgradePreviewRequestDTO {
  const dto = new InternalAuditMonitoringUpgradePreviewRequestDTO();
  dto.sourceTaskIds = [...item.sourceTaskIds];
  dto.auditYear = item.auditYear;
  dto.scope = item.scopeItems ? mapScopeItemsToIds(item.scopeItems) : undefined;

  return dto;
}

function mapFromMonitoringUpgradeSource(
  dto: InternalAuditMonitoringUpgradeSourceDTO,
): InternalAuditMonitoringUpgradeSource {
  const item = new InternalAuditMonitoringUpgradeSource();
  item.sourceTaskId = dto.sourceTaskId;
  item.versionHash = dto.versionHash;
  item.name = dto.name;
  item.description = dto.description;
  item.ownerId = dto.ownerId;
  item.ownerName = dto.ownerName;
  item.assigneeId = dto.assigneeId;
  item.assigneeName = dto.assigneeName;
  item.authSchemaId = dto.authSchemaId;
  item.taskTypeId = dto.taskTypeId;
  item.checklistItemCount = dto.checklistItemCount;
  // the JSX must never print serialized recurrence: parse it here, once, at the edge
  item.recurrencePattern = new RecurringPattern();
  item.recurrencePattern.fromJSON(dto.recurrencePattern);
  item.instanceCount = dto.instanceCount;
  item.completedInstanceCount = dto.completedInstanceCount;
  item.latestCompletedOn = fromApiDateTimeOptional(dto.latestCompletedOn);

  return item;
}

function mapFromMonitoringUpgradeTeamCandidate(
  dto: InternalAuditMonitoringUpgradeTeamCandidateDTO,
): InternalAuditMonitoringUpgradeTeamCandidate {
  const item = new InternalAuditMonitoringUpgradeTeamCandidate();
  item.userId = dto.userId;
  item.name = dto.name;
  item.candidateRoles = dto.candidateRoles ?? [];
  // the API writes null for an undecided candidate; the panel keys on undefined
  item.suggestedRole = dto.suggestedRole ?? undefined;
  item.requiresDecision = dto.requiresDecision;
  item.sourceTaskIds = dto.sourceTaskIds ?? [];
  item.rolePermissions = (dto.rolePermissions ?? []).map((permission) => {
    const mapped = new InternalAuditMonitoringUpgradeRolePermission();
    mapped.role = permission.role;
    mapped.granted = permission.granted;
    mapped.reason = permission.reason;
    mapped.authSchemaName = permission.authSchemaName;

    return mapped;
  });

  return item;
}

function mapFromMonitoringUpgradeScopeSource(
  dto: InternalAuditMonitoringUpgradeScopeSourceDTO,
): InternalAuditMonitoringUpgradeScopeSource {
  const item = new InternalAuditMonitoringUpgradeScopeSource();
  item.entityType = dto.entityType;
  item.entityId = dto.entityId;
  item.sourceTaskIds = dto.sourceTaskIds ?? [];

  return item;
}

function mapFromMonitoringUpgradeProcedureSource(
  dto: InternalAuditMonitoringUpgradeProcedureSourceDTO,
): InternalAuditMonitoringUpgradeProcedureSource {
  const item = new InternalAuditMonitoringUpgradeProcedureSource();
  item.sourceTaskId = dto.sourceTaskId;
  item.sourceName = dto.sourceName;
  item.checklistItemCount = dto.checklistItemCount;
  item.assigneeId = dto.assigneeId;
  item.assigneeName = dto.assigneeName;

  return item;
}

function mapFromMonitoringUpgradeProcedureScopeItem(
  dto: InternalAuditMonitoringUpgradeProcedureScopeItemDTO,
): InternalAuditMonitoringUpgradeProcedureScopeItem {
  const item = new InternalAuditMonitoringUpgradeProcedureScopeItem();
  item.entityType = dto.entityType;
  item.entityId = dto.entityId;
  item.code = dto.code;
  item.name = dto.name;

  return item;
}

function mapFromMonitoringUpgradeProcedure(
  dto: InternalAuditMonitoringUpgradeProcedureDTO,
): InternalAuditMonitoringUpgradeProcedure {
  const item = new InternalAuditMonitoringUpgradeProcedure();
  item.key = dto.key;
  item.name = dto.name;
  item.taskTypeId = dto.taskTypeId;
  item.formName = dto.formName;
  item.checklistItemCount = dto.checklistItemCount;
  item.sources = (dto.sources ?? []).map(mapFromMonitoringUpgradeProcedureSource);
  item.scopeItems = (dto.scopeItems ?? []).map(mapFromMonitoringUpgradeProcedureScopeItem);

  return item;
}

export function mapFromInternalAuditMonitoringUpgradePreview(
  dto: InternalAuditMonitoringUpgradePreviewDTO,
): InternalAuditMonitoringUpgradePreview {
  const item = new InternalAuditMonitoringUpgradePreview();
  item.auditYear = dto.auditYear;
  item.suggestedName = dto.suggestedName;
  item.suggestedDescription = dto.suggestedDescription;
  item.suggestedAuditType = dto.suggestedAuditType;
  item.suggestedAuthSchemaId = dto.suggestedAuthSchemaId ?? undefined;
  item.authSchemaRequiresSelection = dto.authSchemaRequiresSelection;
  item.sources = (dto.sources ?? []).map(mapFromMonitoringUpgradeSource);
  item.teamCandidates = (dto.teamCandidates ?? []).map(mapFromMonitoringUpgradeTeamCandidate);
  item.teamRequiresResolution = dto.teamRequiresResolution;
  item.proposedScope = mapFromInternalAuditScope(dto.proposedScope);
  item.scopeSources = (dto.scopeSources ?? []).map(mapFromMonitoringUpgradeScopeSource);
  item.procedures = (dto.procedures ?? []).map(mapFromMonitoringUpgradeProcedure);
  item.sourcesWithoutScope = dto.sourcesWithoutScope ?? [];
  item.suggestedRecurrencePattern = dto.suggestedRecurrencePattern;
  item.recurrenceCompatible = dto.recurrenceCompatible;
  item.warnings = dto.warnings ?? [];
  item.existingInternalAuditTemplateId = dto.existingInternalAuditTemplateId;
  item.existingInternalAuditId = dto.existingInternalAuditId;

  return item;
}

export function mapToInternalAuditMonitoringUpgradeRequest(
  item: InternalAuditMonitoringUpgradeRequest,
): InternalAuditMonitoringUpgradeRequestDTO {
  const dto = new InternalAuditMonitoringUpgradeRequestDTO();
  dto.clientRequestId = item.clientRequestId;
  dto.template = mapToInternalAuditTemplate(item.template);
  dto.sources = item.sources.map((source) => {
    const sourceDto = new InternalAuditMonitoringUpgradeSourceVersionDTO();
    sourceDto.sourceTaskId = source.sourceTaskId;
    sourceDto.versionHash = source.versionHash;

    return sourceDto;
  });
  dto.scope = mapScopeItemsToIds(item.scopeItems);
  dto.auditYear = item.auditYear;

  return dto;
}

export function mapFromInternalAuditMonitoringUpgradeResult(
  dto: InternalAuditMonitoringUpgradeResultDTO,
): InternalAuditMonitoringUpgradeResult {
  const item = new InternalAuditMonitoringUpgradeResult();
  item.internalAuditTemplateId = dto.internalAuditTemplateId;
  item.internalAuditId = dto.internalAuditId;
  item.existing = dto.existing;
  item.warnings = dto.warnings ?? [];

  return item;
}

export function mapFromInternalAuditMonitoringSource(
  dto: InternalAuditMonitoringSourceDTO,
): InternalAuditMonitoringSource {
  const item = new InternalAuditMonitoringSource();
  item.sourceTaskId = dto.sourceTaskId;
  item.name = dto.name;
  item.upgradedOn = fromApiDateTimeOptional(dto.upgradedOn);
  item.auditYear = dto.auditYear;
  item.deactivationResult = dto.deactivationResult;
  item.completedInstanceCount = dto.completedInstanceCount;
  item.latestCompletedOn = fromApiDateTimeOptional(dto.latestCompletedOn);

  return item;
}
